// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Block, InlineNode, tokenizeInline, tokenizeMarkdown } from './markdown';
import type { ChatMessage } from './chatbotTypes';

// Download a Khora chat — a single response or the whole session — as Markdown, HTML, plain text, or
// JSON. All client-side (Blob + anchor), so it works for any message already on screen. Every format
// gets a branded header + metadata; the HTML is a full AirWorks-branded, typographic light-report
// document that RENDERS Khora's markdown (headers, tables, lists, code, links) — never raw syntax.
export type ExportFormat = 'md' | 'html' | 'txt' | 'json';

const roleLabel = (role: string): string => (role === 'user' ? 'You' : 'Khora');

const messageText = (message: ChatMessage): string => {
  if (typeof message.content === 'string') return message.content;
  return message.content.map((block) => (block.type === 'text' ? block.text : '')).join('\n').trim();
};

// Every image on a message: any attached content-block image (defensive for restored sessions).
const messageImages = (message: ChatMessage): string[] => {
  const urls: string[] = [];
  if (Array.isArray(message.content)) {
    message.content.forEach((block) => {
      if (block.type === 'image') urls.push(`data:${block.source.media_type};base64,${block.source.data}`);
    });
  }
  return urls;
};

const exportedStamp = (): string => new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
const plural = (count: number): string => (count === 1 ? '' : 's');

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (value: string): string => escapeHtml(value).replace(/"/g, '&quot;');
// Only http(s)/mailto links become anchors; anything else renders as plain text (no javascript:/data:).
const safeHref = (url: string): string | null => (/^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : null);

// ---- token tree -> HTML (safe: escaped text, allow-listed link schemes, no raw HTML) --------------
// Walks the SHARED markdown token tree (./markdown) that ChatMarkdown renders to React, so the exported
// document and the in-chat view can't drift. Escaping + the link allow-list are applied HERE.
const renderInlineHtml = (nodes: InlineNode[]): string => nodes.map((node) => {
  if (node.type === 'text') return escapeHtml(node.value);
  if (node.type === 'code') return `<code>${escapeHtml(node.value)}</code>`;
  if (node.type === 'bold') return `<strong>${renderInlineHtml(node.children)}</strong>`;
  if (node.type === 'italic') return `<em>${renderInlineHtml(node.children)}</em>`;
  // link — safeHref rejects anything but http(s)/mailto, in which case it renders as plain escaped text.
  const href = safeHref(node.href);
  return href ? `<a href="${escapeAttr(href)}">${escapeHtml(node.value)}</a>` : escapeHtml(node.value);
}).join('');

// One block token -> an HTML string. Khora's answers use ### as their top-level section heading, so
// #..### all map to the most prominent style (h3); #### -> h4; deeper -> h5.
const renderBlockHtml = (block: Block): string => {
  if (block.type === 'code') return `<pre><code>${escapeHtml(block.text)}</code></pre>`;
  if (block.type === 'table') {
    const thead = `<tr>${block.header.map((cell) => `<th>${renderInlineHtml(tokenizeInline(cell))}</th>`).join('')}</tr>`;
    const tbody = block.rows.map((row) => `<tr>${row.map((cell) => `<td>${renderInlineHtml(tokenizeInline(cell))}</td>`).join('')}</tr>`).join('');
    return `<div class="table-wrap"><table><thead>${thead}</thead><tbody>${tbody}</tbody></table></div>`;
  }
  if (block.type === 'heading') {
    let tag = 'h5';
    if (block.level <= 3) tag = 'h3';
    else if (block.level === 4) tag = 'h4';
    return `<${tag}>${renderInlineHtml(tokenizeInline(block.text))}</${tag}>`;
  }
  if (block.type === 'hr') return '<hr/>';
  if (block.type === 'blockquote') return `<blockquote>${renderInlineHtml(tokenizeInline(block.text))}</blockquote>`;
  if (block.type === 'list') {
    const tag = block.ordered ? 'ol' : 'ul';
    return `<${tag}>${block.items.map((item) => `<li>${renderInlineHtml(tokenizeInline(item))}</li>`).join('')}</${tag}>`;
  }
  // paragraph
  return `<p>${renderInlineHtml(tokenizeInline(block.text)).replace(/\n/g, '<br>')}</p>`;
};

// Exported for unit tests — the escaping + link-scheme allow-list are the security core of the export.
export const markdownToHtml = (markdown: string): string => tokenizeMarkdown(markdown).map(renderBlockHtml).join('\n');

// User text is shown verbatim (escaped); Khora's answer is rendered from markdown. Charts follow.
const renderMessageBody = (message: ChatMessage): string => {
  const text = messageText(message);
  const body = message.role === 'assistant'
    ? markdownToHtml(text)
    : `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`;
  const images = messageImages(message)
    .map((url) => `<img class="chart" src="${escapeAttr(url)}" alt="chart"/>`).join('');
  return body + images;
};

// AirWorks light-report stylesheet — Midnight Blue #153B5A, Bright Blue #01A1DD, Light Blue #B9E2F1; Roboto stack;
// print/PDF-friendly. Self-contained (no external fonts/assets) so the downloaded file renders anywhere.
const EXPORT_CSS = `
*{box-sizing:border-box}
body{margin:0;background:#eef2f6;color:#24323f;font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;-webkit-font-smoothing:antialiased}
.doc{max-width:820px;margin:24px auto;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 4px 24px rgba(21,59,90,.10)}
.doc-header{background:#153B5A;padding:22px 32px;border-bottom:4px solid #01A1DD}
.brand{font-size:22px;font-weight:300;letter-spacing:.3px;color:#fff}
.brand span{color:#B9E2F1;font-weight:500}
.meta{margin-top:4px;font-size:12.5px;color:#9fb3c4;letter-spacing:.2px}
main{padding:26px 32px}
.turn{padding:14px 0 18px;border-bottom:1px solid #eef2f6}
.turn:last-child{border-bottom:none}
.speaker{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.8px;margin-bottom:8px;color:#5b6b7a}
.turn-khora .speaker{color:#01A1DD}
.body>*:first-child{margin-top:0}
.body>*:last-child{margin-bottom:0}
.body p{margin:0 0 10px}
.body h3{font-size:16px;font-weight:600;color:#153B5A;margin:16px 0 6px}
.body h4{font-size:14.5px;font-weight:600;color:#153B5A;margin:14px 0 6px}
.body h5{font-size:13px;font-weight:600;color:#334a5e;margin:12px 0 4px}
.body ul,.body ol{margin:6px 0 10px;padding-left:22px}
.body li{margin:3px 0}
.body a{color:#01A1DD;text-decoration:none;border-bottom:1px solid rgba(1,161,221,.35)}
.body code{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-size:.86em;background:#f0f4f8;padding:1px 5px;border-radius:4px;color:#22506b}
.body pre{background:#f6f9fb;border:1px solid #e2e8ee;border-radius:8px;padding:12px 14px;overflow-x:auto;margin:10px 0}
.body pre code{background:none;padding:0;color:#24323f}
.body blockquote{margin:10px 0;padding:4px 14px;border-left:3px solid #01A1DD;color:#5b6b7a;background:#f6f9fb;border-radius:0 6px 6px 0}
.body hr{border:none;border-top:1px solid #e2e8ee;margin:16px 0}
.table-wrap{overflow-x:auto;margin:10px 0}
.body table{border-collapse:collapse;font-size:13.5px;width:auto;min-width:50%}
.body th,.body td{border:1px solid #e2e8ee;padding:6px 12px;text-align:left;vertical-align:top}
.body th{background:#eef4f9;color:#153B5A;font-weight:600;white-space:nowrap}
.body tbody tr:nth-child(even){background:#f9fbfd}
.body img.chart{display:block;max-width:100%;height:auto;border:1px solid #e2e8ee;border-radius:8px;margin:12px 0}
footer{padding:16px 32px;border-top:1px solid #eef2f6;color:#9fb3c4;font-size:11.5px;text-align:center}
@media print{body{background:#fff}.doc{box-shadow:none;margin:0;max-width:none;border-radius:0}.turn{break-inside:avoid}.table-wrap,.body img.chart,.body pre{break-inside:avoid}}
@media (max-width:640px){main,.doc-header,footer{padding-left:18px;padding-right:18px}}
`;

const toHtml = (messages: ChatMessage[]): string => {
  const count = messages.length;
  const turns = messages.map((message) => {
    const cls = message.role === 'user' ? 'user' : 'khora';
    return `<section class="turn turn-${cls}"><div class="speaker">${roleLabel(message.role)}</div>`
      + `<div class="body">${renderMessageBody(message)}</div></section>`;
  }).join('\n');
  return '<!doctype html><html lang="en"><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=device-width, initial-scale=1">'
    + '<title>Khora — chat export</title>'
    + `<style>${EXPORT_CSS}</style></head><body><div class="doc">`
    + '<header class="doc-header"><div class="brand">AirWorks <span>Khora</span></div>'
    + `<div class="meta">Chat export · ${escapeHtml(exportedStamp())} · ${count} message${plural(count)}</div></header>`
    + `<main>${turns}</main>`
    + '<footer>Generated by Khora · AirWorks Solutions, Inc.</footer>'
    + '</div></body></html>';
};

const toMarkdown = (messages: ChatMessage[]): string => {
  const lines = [
    '# Khora — chat export',
    '',
    `_Exported ${exportedStamp()} · ${messages.length} message${plural(messages.length)}_`,
    '',
    '---',
    '',
  ];
  messages.forEach((message) => {
    lines.push(`### ${roleLabel(message.role)}`, '', messageText(message), '');
    messageImages(message).forEach((url) => lines.push(`![chart](${url})`, ''));
  });
  return lines.join('\n');
};

const toText = (messages: ChatMessage[]): string => {
  const rule = '='.repeat(56);
  const sep = '-'.repeat(56);
  const header = `Khora — chat export\nExported ${exportedStamp()} · ${messages.length} message${plural(messages.length)}\n${rule}\n`;
  const body = messages.map((message) => {
    const images = messageImages(message).length;
    return `${roleLabel(message.role).toUpperCase()}\n${messageText(message)}${
      images ? `\n[${images} chart image(s) omitted — use the HTML export to keep them]` : ''}`;
  }).join(`\n\n${sep}\n\n`);
  return `${header}\n${body}\n`;
};

const toJson = (messages: ChatMessage[]): string => JSON.stringify({
  app: 'Khora',
  exportedAt: new Date().toISOString(),
  messageCount: messages.length,
  messages: messages.map((message) => ({
    role: message.role,
    speaker: roleLabel(message.role),
    text: messageText(message),
    images: messageImages(message),
    // The structured artifacts Khora produced — machine-readable, so a JSON export isn't prose-only.
    // (Undefined keys are dropped by JSON.stringify.) Files carry metadata only, never the inline b64 blob.
    charts: message.charts,
    widgets: message.widgets,
    cards: message.cards,
    feature_analyses: message.feature_analyses,
    files: message.files?.map((file) => ({ name: file.name, mime: file.mime })),
  })),
}, null, 2);

const FORMATS: Record<ExportFormat, { mime: string; ext: string; build: (messages: ChatMessage[]) => string }> = {
  md: { mime: 'text/markdown', ext: 'md', build: toMarkdown },
  html: { mime: 'text/html', ext: 'html', build: toHtml },
  txt: { mime: 'text/plain', ext: 'txt', build: toText },
  json: { mime: 'application/json', ext: 'json', build: toJson },
};

const downloadBlob = (filename: string, content: string, mime: string): void => {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

// Build the export string for `format` without triggering a download — the testable seam behind exportChat.
export const buildExport = (messages: ChatMessage[], format: ExportFormat): string => FORMATS[format].build(messages);

// Download `messages` in `format`. `scope` only flavors the filename (whole chat vs. one response).
export const exportChat = (messages: ChatMessage[], format: ExportFormat, scope: 'session' | 'message'): void => {
  const { mime, ext, build } = FORMATS[format];
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  downloadBlob(`khora-${scope === 'session' ? 'chat' : 'response'}-${stamp}.${ext}`, build(messages), mime);
};
